import React, { useState } from 'react';
import { Button, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { getRowAction, ACTIONS, VIEWS } from '../helpers/statusConfig';

const useStyles = makeStyles(() => ({
  btn: {
    textTransform: 'none',
    borderRadius: 20,
    minWidth: 170,
    padding: '4px 14px',
    fontSize: '0.78rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    backgroundColor: '#FFFFFF',
    color: '#000000',
    border: '1px solid #000000',
    boxShadow: 'none',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: '#F4F6FB',
      borderColor: '#000000',
    },
  },
  disabled: {
    backgroundColor: '#FFFFFF',
    color: '#9E9E9E',
    border: '1px solid #BDBDBD',
  },
  comment: {
    backgroundColor: '#000000',
    color: '#FFFFFF',
    border: 'none',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#222222',
      borderColor: 'transparent',
    },
  },
  menuItem: {
    fontSize: '0.82rem',
    minWidth: 170,
  },
}));

const CHOOSE_MENU_ITEMS = [
  { key: 'VIEW_SHIPMENT_DETAILS', label: 'View Shipment Details' },
  { key: 'COMMENTS',              label: 'Comments'              },
];

const POST_SCHEDULE_MENU_ITEMS = [
  ...CHOOSE_MENU_ITEMS,
  { key: 'UPDATE_SHIPMENT', label: 'Update Shipment', disabled: true },
  { key: 'CANCEL_SHIPMENT', label: 'Cancel Shipment', disabled: true },
];

// A "Shipment Scheduled" (statusID 63) row in the Post-Schedule view gains a
// "Sync Status" action (upload the freight-forwarder document → moves to Shipped),
// mirroring the "Sync Status" action on a Shipped row in the Post-Shipment view.
const POST_SCHEDULE_SCHEDULED_MENU_ITEMS = [
  { key: 'VIEW_SHIPMENT_DETAILS', label: 'View Shipment Details' },
  { key: 'SYNC_STATUS',           label: 'Sync Status'           },
  { key: 'COMMENTS',              label: 'Comments'              },
  { key: 'UPDATE_SHIPMENT', label: 'Update Shipment', disabled: true },
  { key: 'CANCEL_SHIPMENT', label: 'Cancel Shipment', disabled: true },
];

// Post-Shipment (Post-Delivery) view exposes only the disabled "Return Shipment"
// action alongside the default choose items — no Update / Cancel Shipment here.
const POST_DELIVERY_MENU_ITEMS = [
  ...CHOOSE_MENU_ITEMS,
  { key: 'RETURN_SHIPMENT', label: 'Return Shipment', disabled: true },
];

// A "Shipped" (statusID 64) row shown in the Post-Shipment view gains a
// "Sync Status" action (upload the freight-forwarder XML → moves to Goods Received).
const POST_DELIVERY_SHIPPED_MENU_ITEMS = [
  { key: 'VIEW_SHIPMENT_DETAILS', label: 'View Shipment Details' },
  { key: 'SYNC_STATUS',           label: 'Sync Status'           },
  { key: 'COMMENTS',              label: 'Comments'              },
  { key: 'RETURN_SHIPMENT',       label: 'Return Shipment', disabled: true },
];

const SPLIT_MENU_ITEMS = {
  [ACTIONS.SCHEDULE_SHIPMENT]:      [{ key: 'COMMENTS', label: 'Comments' }],
  [ACTIONS.GENERATE_PROFORMA]:      CHOOSE_MENU_ITEMS,
  [ACTIONS.GENERATE_CONSIGNEE_GRN]: CHOOSE_MENU_ITEMS,
};

const RowActionButton = ({ row, role, view, onAction, onComment }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const isCmo = role === 'CMO';

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleMenuSelect = (key) => {
    handleCloseMenu();
    if (key === 'COMMENTS') {
      onComment && onComment(row);
    } else {
      onAction && onAction(row, key);
    }
  };

  if (isCmo) {
    return (
      <Button
        className={`${classes.btn} ${classes.comment}`}
        onClick={() => onComment && onComment(row)}
      >
        Comments
      </Button>
    );
  }

  const statusAction = getRowAction(row.statusId || row.statusID, view);
  // showGenerateDocumentAction from API controls whether "Generate Proforma" is available.
  // true  → force Generate Proforma button
  // false → if status-based action would be Generate Proforma, fall back to Choose an action
  // null/undefined → use status-based logic unchanged
  const action = row.showGenerateDocumentAction === true && statusAction.action === 'GENERATE_PROFORMA'
    ? { label: 'Generate Proforma', enabled: true, action: 'GENERATE_PROFORMA', style: 'outline' }
    : !row.showGenerateDocumentAction && statusAction.action === 'GENERATE_PROFORMA'
      ? { label: 'Choose the action', enabled: true, action: 'CHOOSE_ACTION', style: 'choose' }
      : statusAction;

  const statusId = row.statusId || row.statusID;
  const isChoose = action.style === 'choose';
  const isSplit  = action.style === 'outline';
  const menuItems = isChoose
    ? (view === VIEWS.POST_SCHEDULE
        // Shipment Scheduled (63) exposes "Sync Status"; other Post-Schedule statuses don't.
        ? (statusId === 63 ? POST_SCHEDULE_SCHEDULED_MENU_ITEMS : POST_SCHEDULE_MENU_ITEMS)
        : view === VIEWS.POST_DELIVERY
          ? (statusId === 64 ? POST_DELIVERY_SHIPPED_MENU_ITEMS : POST_DELIVERY_MENU_ITEMS)
          : CHOOSE_MENU_ITEMS)
    : (SPLIT_MENU_ITEMS[action.action] || []);

  const handleArrowClick = (e) => {
    e.stopPropagation();
    handleOpenMenu(e);
  };

  return (
    <>
      <Button
        className={`${classes.btn} ${!action.enabled ? classes.disabled : ''}`}
        style={{ justifyContent: 'center' }}
        disabled={!action.enabled}
        endIcon={
          (isChoose || isSplit) ? (
            <span
              onClick={isSplit ? handleArrowClick : undefined}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <ArrowDropDownIcon />
            </span>
          ) : null
        }
        onClick={
          isChoose
            ? handleOpenMenu
            : () => action.enabled && onAction && onAction(row, action.action)
        }
      >
        {action.label}
      </Button>

      {(isChoose || isSplit) && menuItems.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.key}
              className={classes.menuItem}
              disabled={!!item.disabled}
              onClick={() => !item.disabled && handleMenuSelect(item.key)}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default React.memo(RowActionButton);
